<template>
  <v-card outlined class="coverage-preview">
    <v-card-title class="subtitle-1">Coverage preview</v-card-title>
    <v-card-text>
      <p v-if="!hasSelection" class="mb-0">
        Select an anchor suburb and radius to see the suburbs covered.
      </p>
      <div v-else-if="loading" role="status" class="py-4">
        <v-progress-circular indeterminate size="20" width="2" class="mr-2" />
        Loading covered suburbs…
      </div>
      <v-alert v-else-if="error" type="error" outlined dense role="alert">
        {{ error }}
        <v-btn small text @click="queuePreview">Retry</v-btn>
      </v-alert>

      <div v-if="preview">
        <p aria-live="polite" class="mb-2">
          <strong>{{ preview.total }} suburbs covered</strong> from
          {{ preview.anchor.name }}.
        </p>
        <p class="text-caption">
          Coverage selects the nearest suburbs: 100 for the 10 km tier, 200 for
          20 km, 300 for 30 km, and 395 for 40 km. It can extend beyond the tier's
          distance, but only includes suburbs within 40 km of Melbourne CBD.
          Fewer suburbs are included if fewer are available. Markers show suburb
          locations, not suburb boundaries.
        </p>
        <v-alert
          v-if="!hasCoordinates(preview.anchor)"
          type="info"
          outlined
          dense
        >
          The anchor suburb has no map coordinates. Coverage cannot be
          calculated until its coordinates are available.
        </v-alert>
        <v-alert v-if="tileWarning" type="info" outlined dense>
          Some map tiles could not load. You can still use the covered suburb
          list.
        </v-alert>
      </div>

      <v-row v-show="preview">
        <v-col cols="12" md="8">
          <div
            v-show="hasMapLocations"
            ref="map"
            class="coverage-preview__map"
            role="region"
            aria-label="Map of the anchor suburb and covered suburbs"
          ></div>
          <div
            v-if="hasMapLocations"
            class="d-flex flex-wrap align-center mt-2"
          >
            <span class="mr-4">
              <span class="coverage-preview__legend-anchor" aria-hidden="true"
                >◆</span
              >
              Anchor suburb
            </span>
            <span class="mr-4">
              <span class="coverage-preview__legend-suburb" aria-hidden="true"
                >●</span
              >
              Covered suburb
            </span>
            <v-btn small text @click="fitCoverage">Show all</v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            label="Find a covered suburb or postcode"
            outlined
            dense
            clearable
            hide-details
            prepend-inner-icon="mdi-magnify"
          />
          <p class="text-caption mt-2 mb-1" aria-live="polite">
            {{ filteredSuburbs.length }} of
            {{ preview ? preview.total : 0 }} suburbs
          </p>
          <v-list
            dense
            class="coverage-preview__list"
            aria-label="Covered suburbs"
          >
            <v-list-item
              v-for="suburb in filteredSuburbs"
              :key="suburb.id"
              :input-value="selectedId === suburb.id"
              :disabled="!hasCoordinates(suburb)"
              color="primary"
              @click="focusSuburb(suburb)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ suburb.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ suburb.postcode }}
                  <span v-if="preview && suburb.id === preview.anchor.id">
                    · Anchor</span
                  >
                  <span v-if="!hasCoordinates(suburb)"> · Map unavailable</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-icon small v-if="hasCoordinates(suburb)"
                >mdi-map-marker</v-icon
              >
            </v-list-item>
            <p v-if="!filteredSuburbs.length" class="pa-3 mb-0">
              {{
                search
                  ? 'No covered suburbs match your search.'
                  : 'No suburbs are covered.'
              }}
            </p>
          </v-list>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import * as L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { axios } from '../../../utils'

export default {
  name: 'CoveragePreview',
  props: {
    anchorSuburb: { type: String, default: '' },
    radiusLevel: { type: [Number, String], default: null }
  },
  data() {
    return {
      preview: null,
      search: '',
      loading: false,
      error: '',
      tileWarning: false,
      selectedId: null,
      requestVersion: 0,
      debounceTimer: null
    }
  },
  computed: {
    hasSelection() {
      return (
        !!this.anchorSuburb && [1, 2, 3, 4].includes(Number(this.radiusLevel))
      )
    },
    selectionKey() {
      return JSON.stringify([this.anchorSuburb, this.radiusLevel])
    },
    filteredSuburbs() {
      if (!this.preview) return []
      const query = (this.search || '').trim().toLowerCase()
      return this.preview.suburbs.filter(suburb =>
        `${suburb.name} ${suburb.postcode || ''}`.toLowerCase().includes(query)
      )
    },
    hasMapLocations() {
      return (
        !!this.preview &&
        (this.hasCoordinates(this.preview.anchor) ||
          this.preview.suburbs.some(this.hasCoordinates))
      )
    }
  },
  watch: {
    selectionKey() {
      this.queuePreview()
    }
  },
  mounted() {
    // Keep Leaflet objects outside Vue's reactive state.
    this.coverageMap = null
    this.suburbMarkers = new Map()
    this.queuePreview()
  },
  beforeDestroy() {
    this.requestVersion++
    clearTimeout(this.debounceTimer)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.coverageMap) this.coverageMap.remove()
  },
  methods: {
    hasCoordinates(suburb) {
      return (
        !!suburb &&
        Number.isFinite(suburb.latitude) &&
        Number.isFinite(suburb.longitude) &&
        Math.abs(suburb.latitude) <= 90 &&
        Math.abs(suburb.longitude) <= 180
      )
    },
    queuePreview() {
      clearTimeout(this.debounceTimer)
      const version = ++this.requestVersion
      this.preview = null
      this.error = ''
      this.search = ''
      this.selectedId = null
      this.loading = this.hasSelection
      if (this.coverageLayer) this.coverageLayer.clearLayers()
      if (!this.hasSelection) return

      this.debounceTimer = setTimeout(() => this.loadPreview(version), 250)
    },
    async loadPreview(version) {
      try {
        const { data } = await axios.get(
          '/api/admin/booking-invoices/coverage-preview',
          {
            params: {
              anchor_suburb: this.anchorSuburb,
              radius_level: this.radiusLevel
            }
          }
        )
        // A slower response must never replace a newer selection.
        if (version !== this.requestVersion) return
        this.preview = data
        await this.$nextTick()
        if (version === this.requestVersion) this.renderMap()
      } catch (error) {
        if (version !== this.requestVersion) return
        this.preview = null
        this.error =
          'Unable to load coverage. Please check the selected suburb and try again.'
      } finally {
        if (version === this.requestVersion) this.loading = false
      }
    },
    renderMap() {
      if (!this.hasMapLocations) return
      if (!this.coverageMap) {
        this.coverageMap = L.map(this.$refs.map, {
          scrollWheelZoom: false,
          preferCanvas: true
        })
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        })
          .on('tileerror', () => {
            this.tileWarning = true
          })
          .addTo(this.coverageMap)
        this.coverageLayer = L.featureGroup().addTo(this.coverageMap)
        if (typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(() => {
            if (this.coverageMap) this.coverageMap.invalidateSize()
          })
          this.resizeObserver.observe(this.$refs.map)
        }
      }

      this.coverageLayer.clearLayers()
      this.suburbMarkers.clear()
      this.preview.suburbs.forEach(suburb => {
        if (!this.hasCoordinates(suburb)) return
        const marker = L.circleMarker([suburb.latitude, suburb.longitude], {
          radius: 5,
          color: '#1565c0',
          weight: 1,
          fillColor: '#2196f3',
          fillOpacity: 0.8
        })
        this.addSuburbMarker(marker, suburb, false)
      })

      const anchor = this.preview.anchor
      if (this.hasCoordinates(anchor)) {
        const marker = L.marker([anchor.latitude, anchor.longitude], {
          title: `Anchor: ${anchor.name}`,
          icon: L.divIcon({
            className: 'coverage-preview__anchor',
            html: '<span></span>',
            iconSize: [26, 34],
            iconAnchor: [13, 34],
            popupAnchor: [0, -32]
          }),
          zIndexOffset: 1000
        })
        this.addSuburbMarker(marker, anchor, true)
      }
      this.fitCoverage()
    },
    addSuburbMarker(marker, suburb, isAnchor) {
      const popup = document.createElement('div')
      popup.textContent = `${isAnchor ? 'Anchor: ' : ''}${
        suburb.name
      } ${suburb.postcode || ''}`
      marker.bindPopup(popup)
      marker.on('click', () => {
        this.selectedId = suburb.id
      })
      marker.addTo(this.coverageLayer)
      this.suburbMarkers.set(suburb.id, marker)
    },
    fitCoverage() {
      if (!this.coverageMap || !this.coverageLayer) return
      this.coverageMap.invalidateSize()
      const bounds = this.coverageLayer.getBounds()
      if (bounds.isValid()) {
        this.coverageMap.fitBounds(bounds, { padding: [28, 28], maxZoom: 13 })
      }
    },
    focusSuburb(suburb) {
      const marker = this.suburbMarkers.get(suburb.id)
      if (!marker || !this.coverageMap) return
      this.selectedId = suburb.id
      this.coverageMap.setView(marker.getLatLng(), 13)
      marker.openPopup()
    }
  }
}
</script>

<style>
.coverage-preview__map {
  height: 360px;
  width: 100%;
  border-radius: 4px;
  z-index: 0;
}
.coverage-preview__list {
  max-height: 300px;
  overflow-y: auto;
}
.coverage-preview__legend-anchor {
  color: #e65100;
}
.coverage-preview__legend-suburb {
  color: #1565c0;
}
.coverage-preview__anchor span {
  display: block;
  width: 26px;
  height: 26px;
  background: #e65100;
  border: 3px solid #fff;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
}
@media (max-width: 600px) {
  .coverage-preview__map {
    height: 280px;
  }
}
</style>
